import React, {useEffect, useMemo} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';

import {IStore} from 'redux/interface';

import Loader from 'components/loader';
import {SessionCard} from '../../components/session-card/SessionCard';
import {useGetTranslations} from '../../utils/hooks/useGetTranslations';
import {Pages, TranslationKeys} from '../../utils/constants/routeConstants';
import {
  getSessions,
  getTranslations,
  sessionsAsyncActions
} from '../../redux/pageAsyncActions';

import XpeditionIcon from 'assets/image/menu-auth/xpedition.png';
import ProgressBar from '../../components/progress-bar';
import NotFoundInfo from '../../components/not-found-info';
import NotFoundImg from '../../assets/image/common/notFound.png';

import './styles.scss';

export const Sessions: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const {sessions} = useSelector((state: IStore) => state.sessions);
  const {settings} = useSelector((state: IStore) => state.auth);
  const {loading} = useSelector((state: IStore) => state.status);

  const pageConfig = useMemo(() => {
    return settings?.pages.find(
      (p) => p.name.toLowerCase() === Pages.Sessions.toLowerCase()
    );
  }, [settings]);

  const translationKey = pageConfig?.translationKey || TranslationKeys.Sessions;
  const title = pageConfig?.title || Pages.Sessions;

  useGetTranslations({translationKey});

  useEffect(() => {
    dispatch(sessionsAsyncActions[getSessions]());
  }, [dispatch]);

  const currentSession = useMemo(() => {
    for (let i = sessions.length - 1; i >= 0; i--) {
      if (!!sessions[i].dialogId && sessions[i].percentage < 100) {
        return sessions[i];
      }
    }

    return null;
  }, [sessions]);

  const overallProgress = useMemo(() => {
    if (!sessions.length) return 0;

    const total = sessions.reduce((sum, session) => sum + session.percentage, 0);

    return Math.round(total / sessions.length);
  }, [sessions]);

  const completedCount = useMemo(
    () => sessions.filter((session) => session.percentage === 100).length,
    [sessions]
  );

  const isTranslationsLoading = loading.includes(getTranslations);
  const isSessionsLoading = loading.includes(getSessions);

  if (isTranslationsLoading) {
    return <section className="sessions container">
      <Loader/>
    </section>;
  }

  return (
    <section className="sessions container">
      <h1 className="sessions__title">
        <img src={XpeditionIcon} alt="" className="sessions__icon" />
        <FormattedMessage
          id={`gritx.common.navigation.${title}`}
          defaultMessage={title}
        />
      </h1>

      {isSessionsLoading && <div className="sessions__loader">
        <Loader nested/>
      </div>}

      {!isSessionsLoading && sessions.length === 0 && <div className="sessions__empty">
        <NotFoundInfo
          title={intl.formatMessage({
            id: 'gritx.sessions.notFound',
            defaultMessage: 'Sorry, but nothing can be found.'
          })}
          image={NotFoundImg}
        />
      </div>}

      {!isSessionsLoading && sessions.length > 0 && <>
        <div className="sessions__summary">
          <div className="sessions__summary-top">
            <div>
              <p className="sessions__summary-label">
                <FormattedMessage
                  id={'gritx.sessions.overallProgress'}
                  defaultMessage="Overall progress"
                />
              </p>
              <p className="sessions__summary-value">{overallProgress}%</p>
            </div>

            <div className="sessions__summary-stat">
              <div className="sessions__summary-stat-label">
                <FormattedMessage
                  id={'gritx.sessions.completed'}
                  defaultMessage="Completed"
                />
              </div>
              <div className="sessions__summary-stat-value">
                {completedCount}/{sessions.length}
              </div>
            </div>
          </div>

          <div className="sessions__progress">
            <ProgressBar
              progress={overallProgress}
              progressStyleClass="sessions__progress-bar"
            />
          </div>
        </div>

        <section className="sessions__list">
          {sessions.map((session) => (
            <SessionCard
              key={session.xpeditionId}
              session={session}
              isCurrent={currentSession?.xpeditionId === session.xpeditionId}
            />
          ))}
        </section>
      </>}
    </section>
  );
};
